import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { noop, values } from 'lodash';
// Import Types
import type { CJVisualizationButtonRouteUIModel, CJVisualizationButtonUIModel } from 'types';
// Import Components
import {
    CustomerJourneyVisualizationTemplateGroup,
    CustomerJourneyVisualizationButtonRoute,
} from 'components/CustomerJourneyVisualization';
import CustomerJourneyVisualizationToggleSection from './CustomerJourneyVisualizationToggleSection';

// Import Helpers
import { itemsIsValid } from 'services/Helpers';
// Import Constants
import { l } from 'constants/common';
// Import Validations
import { CJVisualizationButtonValidationSchema } from 'validators/schemas.validation';
// Import Styles
import styles from './customerJourneyVisualizationButton.module.scss';

interface Props {
    defaultValue?: Nilable<CJVisualizationButtonUIModel>;
    getUpdate?: (data: Partial<CJVisualizationButtonUIModel>) => void;
}

const getDefaultRoute = (): CJVisualizationButtonRouteUIModel => ({
    page: null,
    extraInfo: {},
    isValid: false,
});

const CustomerJourneyVisualizationButton = (props: Props) => {
    const { defaultValue = null, getUpdate = noop } = props;

    const { t } = useTranslation();

    const [showButton, setShowButton] = useState<boolean>(defaultValue?.showButton === true);
    const [templateData, setTemplateData] = useState<AnyObject>(defaultValue?.templates ?? {});
    const [route, setRoute] = useState<CJVisualizationButtonRouteUIModel>(defaultValue?.route ?? getDefaultRoute());

    const templateChangeHandler = (languageKey, data) => {
        setTemplateData((prev) => {
            return { ...prev, [languageKey]: { ...prev?.[languageKey], ...data } };
        });
    };

    const routeUpdateHandler = (routeValue: CJVisualizationButtonRouteUIModel) => {
        setRoute((prev) => ({ ...prev, ...routeValue }));
    };

    useEffect(() => {
        // When the button is hidden its configuration is not validated and is not sent to the backend
        getUpdate({
            showButton,
            templates: templateData,
            route,
            isValid: !showButton || (itemsIsValid(values(templateData)) && route?.isValid === true),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showButton, templateData, route]);

    return (
        <CustomerJourneyVisualizationToggleSection
            contentClassName={styles.buttonContent}
            label={t(l.ShowButton)}
            description={t(l.ShowButtonDescription)}
            isChecked={showButton}
            onChange={setShowButton}
        >
            <CustomerJourneyVisualizationButtonRoute
                page={route?.page}
                extraInfo={route?.extraInfo}
                getUpdate={routeUpdateHandler}
            />
            <CustomerJourneyVisualizationTemplateGroup
                templates={templateData}
                onChange={templateChangeHandler}
                withDescription={false}
                namePlaceholder={l.ButtonName}
                nameValidationSchema={CJVisualizationButtonValidationSchema}
            />
        </CustomerJourneyVisualizationToggleSection>
    );
};

CustomerJourneyVisualizationButton.displayName = 'CustomerJourneyVisualizationButton';

export default memo(CustomerJourneyVisualizationButton);
